import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { PRODUCT, type ProductCode } from '../../branding';
import { useRoleProfile } from '../../hooks/useRoleProfile';
import { AppDownloadRow } from './GetAppCard';
import { STORE_APPS, appBrandsFor, appPageUrl } from './appStore';

/**
 * "Get your team on the mobile app" — the last step of the admin setup guide, and
 * a Help-menu item.
 *
 * Why it exists: the web admin configures, but the work itself happens on the
 * phone. Attendance is recorded only in the QuTime app (desktop browsers cannot
 * geofence reliably), so an organization whose employees never install it has a
 * finished setup and no records. This dialog is where the admin gets the thing to
 * hand over: a code to show, or a link to paste into the team chat.
 *
 * Shows the apps the organization is licensed for. An app whose listing is not
 * public yet reads "coming soon" here (an admin should know it is on the way), and
 * has no QR or link to copy.
 */
interface Props {
  open: boolean;
  onClose: () => void;
  /** Licence override; default reads the organization's licensed products. */
  licensedProducts?: readonly string[] | null;
}

const LINE_KEY: Record<ProductCode, string> = {
  qutime: 'common:getAppDialog.qutimeLine',
  pureoa: 'common:getAppDialog.pureoaLine',
};

const GetAppDialog: React.FC<Props> = ({ open, onClose, licensedProducts }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useRoleProfile();
  const [copied, setCopied] = useState(false);
  const brands = appBrandsFor(licensedProducts ?? role?.licensedProducts, PRODUCT);
  const liveBrands = brands.filter((b) => STORE_APPS[b].live);

  const copyLinks = async () => {
    const text = liveBrands.map((b) => `${STORE_APPS[b].name}: ${appPageUrl(b)}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>{t('common:getAppDialog.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {t('common:getAppDialog.body')}
        </Typography>
        <Stack spacing={2.5} divider={<Divider flexItem />}>
          {brands.map((brand) => (
            <AppDownloadRow key={brand} brand={brand} qrSize={128} subtitle={t(LINE_KEY[brand])} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          onClick={() => { onClose(); navigate('/organization/workers/active'); }}
          sx={{ textTransform: 'none', marginInlineEnd: 'auto' }}
        >
          {t('common:getAppDialog.inviteWorkers')}
        </Button>
        {liveBrands.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => window.open('/print/app-poster', '_blank')}
            sx={{ textTransform: 'none' }}
          >
            {t('common:getAppDialog.printPoster')}
          </Button>
        )}
        {liveBrands.length > 0 && (
          <Button size="small" variant="outlined" onClick={copyLinks} sx={{ textTransform: 'none' }}>
            {copied ? t('common:getAppDialog.linkCopied') : t('common:getAppDialog.copyLink')}
          </Button>
        )}
        <Button size="small" variant="contained" disableElevation onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GetAppDialog;
